import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'tattoo-studio-near-kypseli';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

export default function TattooStudioNearKypseli() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τατουάζ στην Κυψέλη; Η γειτονιά δίπλα έχει το studio",
      "description": "Μένεις στην Κυψέλη; Το 210 Tattoo Athens απέχει λίγα λεπτά με τα πόδια. Custom, έγχρωμα και old school τατουάζ Αθήνα από έμπειρους artists.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-17",
      "dateModified": "2026-07-17",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-near-kypseli-article', articleSchema);
    return () => {
      document.getElementById('schema-near-kypseli-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ κοντά στην Κυψέλη | 210 Tattoo Athens</title>
        <meta name="description" content="Μένεις στην Κυψέλη; Το 210 Tattoo Athens απέχει λίγα λεπτά με τα πόδια. Custom, έγχρωμα και old school τατουάζ Αθήνα από έμπειρους artists." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Τατουάζ κοντά στην Κυψέλη | 210 Tattoo Athens" />
        <meta property="og:description" content="Λίγα λεπτά με τα πόδια από την Κυψέλη: custom, έγχρωμα και old school τατουάζ Αθήνα." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ κοντά στην Κυψέλη | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Η γειτονιά δίπλα έχει το studio: 12-15 λεπτά με τα πόδια από τη Φωκίωνος Νέγρη." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Τατουάζ κοντά στην Κυψέλη</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Τατουάζ στην Κυψέλη;</span>
              <br />
              <span className="text-white">Η γειτονιά δίπλα έχει το studio</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">17 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Η Κυψέλη είναι από τις πιο ζωντανές και πολυπολιτισμικές γειτονιές της Αθήνας, με ανθρώπους από παντού, γενιές που αλλάζουν και μια αισθητική που δεν κρύβεται σε μπαλκόνια και γωνιές. Αρκετοί από τους πελάτες του 210 Tattoo Athens έρχονται από εκεί, κάτι λογικό αν σκεφτείς ότι το studio μας στη Γκύζη είναι ουσιαστικά η διπλανή γειτονιά. Όσοι ψάχνουν τατουάζ Αθήνα και προτιμούν κάτι κοντά στο σπίτι τους, χωρίς μεγάλες μετακινήσεις, βρίσκουν εδώ μια λύση που λειτουργεί στην πράξη.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Distance */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Η απόσταση, <span className="text-pink-500">με απλά λόγια</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Από την πλατεία Κυψέλης ή τη Φωκίωνος Νέγρη μέχρι την Αναστασίου Γενναδίου 34 είναι μια βόλτα περίπου 12 με 15 λεπτών με τα πόδια. Αν μένεις πιο βόρεια, προς τη Λεωφόρο Πατησίων, δύο με τρεις στάσεις λεωφορείου καλύπτουν την απόσταση εξίσου εύκολα. Είναι μία από τις πιο κοντινές γειτονιές στο studio μας, κάτι που εξηγεί γιατί έχουμε τόσο σταθερή πελατεία από εκεί, πολλοί εκ των οποίων επιστρέφουν και για δεύτερο ή τρίτο τατουάζ.
            </p>
          </div>
        </section>

        {/* What people bring */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι φέρνει ο κόσμος <span className="text-pink-500">από την Κυψέλη</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η ποικιλομορφία της γειτονιάς φαίνεται και στις ιδέες που ακούμε στο consultation. Έχουμε αρκετά αιτήματα για <a href="/color-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">έγχρωμα τατουάζ</a> με έντονα χρώματα και σύνθετες συνθέσεις, αλλά και για πιο κλασικά <a href="/old-school-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">old school σχέδια</a> με bold γραμμές και iconic μοτίβα που δεν βγαίνουν ποτέ από τη μόδα. Όσοι θέλουν κάτι πιο ήσυχο συνήθως στρέφονται σε <a href="/black-gray-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">black &amp; gray</a>, με ομαλές διαβαθμίσεις που κρατούν την αντίθεσή τους στον χρόνο. Και φυσικά, όποια ιδέα δεν χωράει σε καμία έτοιμη κατηγορία τη δουλεύουμε σαν <a href="/custom-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">custom σχέδιο</a>, σχεδιασμένο ειδικά γύρω από αυτό που θέλεις να πεις με το τατουάζ σου.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Πολλοί κάτοικοι της Κυψέλης μας βρίσκουν πρώτα μέσω συστάσεων από φίλους ή γείτονες, κάτι που δείχνει τη σχέση εμπιστοσύνης που έχει χτιστεί με τη γειτονιά τα τελευταία χρόνια.
            </p>
          </div>
        </section>

        {/* A changing neighbourhood */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μια γειτονιά <span className="text-pink-500">που αλλάζει</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Τα τελευταία χρόνια η Κυψέλη έχει γεμίσει με νέους καλλιτέχνες, μικρά studio και καφέ που άνοιξαν γύρω από τη Φωκίωνος Νέγρη, και αυτό φέρνει και πιο τολμηρές αισθητικές επιλογές στο τατουάζ. Ταυτόχρονα, η γειτονιά κρατάει και τον παλιότερο, πιο οικογενειακό της χαρακτήρα, οπότε βλέπουμε εξίσου συχνά και πιο κλασικές, διαχρονικές ιδέες. Αυτή η ποικιλία είναι κάτι που μας αρέσει, γιατί σημαίνει ότι σχεδόν κάθε consultation είναι διαφορετικό από το προηγούμενο.
            </p>
          </div>
        </section>

        {/* Studio principles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ένα studio <span className="text-pink-500">με σταθερές αρχές</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Λειτουργούμε από το 2018 στην Αναστασίου Γενναδίου 34, με πιστοποιημένα μελάνια, αποστειρωμένα εργαλεία μίας χρήσης και προσοχή στη λεπτομέρεια σε κάθε στάδιο, από το consultation μέχρι το τελικό αποτέλεσμα. Αν κάνεις τατουάζ για πρώτη φορά, ρίξε μια ματιά στον οδηγό μας <Link to="/blog/tattoo-aftercare-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">φροντίδα τατουάζ μετά το studio</Link> για να ξέρεις τι να περιμένεις τις πρώτες εβδομάδες.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Πολλοί κάτοικοι της Κυψέλης συνδυάζουν το ραντεβού τους με άλλα θέματα της ημέρας, μια βόλτα στη λαϊκή αγορά, ψώνια στην Πατησίων ή καφέ στη Φωκίωνος Νέγρη πριν ή μετά τη συνεδρία. Η μικρή απόσταση σημαίνει ότι δεν χρειάζεται να αφιερώσεις ολόκληρο το απόγευμά σου μόνο για τη μετακίνηση.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έλα <span className="text-pink-500">από κοντά</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Δεν χρειάζεται να ψάξεις μακριά για ποιοτικό τατουάζ Αθήνα όταν μένεις στην Κυψέλη, το studio είναι ήδη σχεδόν στη γειτονιά σου. Στείλε μήνυμα στο <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">Instagram</a> με την ιδέα σου και κλείσε ραντεβού.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-aftercare-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Φροντίδα Τατουάζ Μετά το Studio</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρεις οδηγίες aftercare για γρήγορη επούλωση.</p>
              </Link>
              <Link to="/blog/first-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Το Πρώτο σου Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να περιμένεις και πώς να προετοιμαστείς σωστά.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/color-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Color Tattoo Athens</a>
              <a href="/old-school-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Old School Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
